import React, { useCallback, useMemo } from 'react';
import type { DataTableRenderCellParams, DataTableRowEventHandler } from '@/components/custom/erp/data-table-v2';
import type { ButtonType } from '@/components/custom/erp/input-table-v2';
import { ErpInputTableV2 } from '@/components/custom/erp';
import { SelectedCellInfo } from '../hooks/useDetailRows';
import { getDetailTableColumns } from './columns';
import { FormMode } from '@/types/form';
import { useTaxRate } from '@/hooks';

interface DetailTabProps {
  formMode: FormMode;
  rows: { uuid?: string | null }[];
  selectedRowUuid?: string | null;
  selectedCell?: SelectedCellInfo | null;
  onRowClick?: DataTableRowEventHandler<BanHangRecord>;
  onCellClick?: (params: DataTableRenderCellParams<BanHangRecord>) => void;
  onAddRow: () => void;
  onDeleteRow: () => void;
  onCopyRow: () => void;
  onPasteRow: (clipboardText?: string) => void | Promise<void>;
  onMoveRow: (direction: 'up' | 'down') => void;
  onCellValueChange: (rowUuid: string, field: string, newValue: BanHangValue) => void;
}

const DETAIL_ACTION_ORDER: ButtonType[] = [
  'add',
  'delete',
  'copy',
  'paste',
  'moveUp',
  'moveDown',
  'export',
  'pin',
  'viewInventory',
  'viewReceipt'
];
const noop = () => {};

export const DetailTab: React.FC<DetailTabProps> = ({
  formMode,
  rows,
  selectedRowUuid,
  selectedCell,
  onRowClick,
  onCellClick,
  onAddRow,
  onDeleteRow,
  onCopyRow,
  onPasteRow,
  onMoveRow,
  onCellValueChange
}) => {
  const { taxRates } = useTaxRate();
  const columns = useMemo(() => getDetailTableColumns(taxRates, onCellValueChange), [onCellValueChange, taxRates]);
  const getRowId = useCallback((row: BanHangRecord) => row?.uuid || '', []);
  const tableActions = useMemo(
    () => ({
      add: onAddRow,
      delete: onDeleteRow,
      copy: onCopyRow,
      paste: onPasteRow,
      moveUp: () => onMoveRow('up'),
      moveDown: () => onMoveRow('down'),
      export: noop,
      pin: noop,
      viewInventory: noop,
      viewReceipt: noop
    }),
    [onAddRow, onCopyRow, onDeleteRow, onMoveRow, onPasteRow]
  );

  return (
    <ErpInputTableV2
      rows={rows}
      onRowClick={onRowClick}
      onCellClick={onCellClick}
      selectedRowId={selectedRowUuid || undefined}
      columns={columns}
      getRowId={getRowId}
      pasteTarget={selectedCell}
      actionsDisabled={formMode === 'view'}
      actions={tableActions}
      actionOrder={DETAIL_ACTION_ORDER}
    />
  );
};
